import React, { useEffect, useMemo, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { SparklesIcon } from "@heroicons/react/24/outline";
import Spinner from "./Spinner";
import { placeholder } from "@cloudinary/react";

interface OpenAiFormProps {
  title: string;
  value: string;
  message: string;
  placeholder: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  error: string | null;
  Submit: () => void;
  multiline?: boolean;
}

const ModalInput: React.FC<OpenAiFormProps> = ({
  title,
  value,
  message,
  placeholder,
  setValue,
  loading,
  error,
  Submit,
  multiline = false,
}) => {
  // On veut de l'autofocusm mais ne marche pas naturellement, donc useEffect + ref
  const inputRef = useRef<null | HTMLInputElement | HTMLTextAreaElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Enter soumet ; en multi-ligne on laisse Enter faire un retour à la ligne
  // et on soumet avec Cmd/Ctrl+Enter.
  const onKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key !== "Enter") return;
    if (multiline && !(e.metaKey || e.ctrlKey)) return;
    e.preventDefault();
    Submit();
  };

  return (
    <form className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center text-lg font-semibold leading-6 text-gray-700">
        <span>{title}</span>
      </div>
      <div className="flex text-start text-base text-gray-500">
        <span className="break-words">{message}</span>
      </div>
      <div className={`relative mt-3 ${multiline ? "flex flex-col gap-2" : "flex items-center"}`}>
        {multiline ? (
          <textarea
            onChange={(e) => setValue(e.target.value)}
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={value}
            rows={5}
            onKeyDown={onKeyDown}
            className="w-full text-sm form-textarea flex flex-1 rounded-md border-gray-300 shadow-sm resize-y focus:border-brand_focus focus:ring focus:ring-brand_focus focus:ring-opacity-20"
            placeholder={placeholder}
          />
        ) : (
          <input
            type="text"
            onChange={(e) => setValue(e.target.value)}
            ref={inputRef as React.RefObject<HTMLInputElement>}
            // autoFocus
            value={value}
            onKeyDown={onKeyDown}
            className="pr-16 w-full text-sm form-input flex flex-1 rounded-md border-gray-300 shadow-sm focus:border-brand_focus focus:ring focus:ring-brand_focus focus:ring-opacity-20 "
            placeholder={placeholder}
          />
        )}
        <button
          type="button"
          onClick={Submit}
          className={`font-medium text-white bg-brand_main border border-gray-300 hover:bg-brand_darker duration-300 ${
            multiline
              ? "self-end px-4 py-2 rounded-md"
              : "absolute -right-0 h-full p-2 rounded-r-md"
          } ${loading ? "ring-4 ring-brand_lighter" : ""} `}
        >
          <span className="mb-2">
            {!loading ? (
              <>
                <FormattedMessage
                  id="url_scrapper.go"
                  defaultMessage="Générer"
                />
              </>
            ) : (
              <Spinner className="animate-spin mb-1 ml-4 h-5 w-5 text-white" />
            )}
          </span>
        </button>
      </div>
      {error ? (
        <div className="mt-2 p-2 bg-red-500 text-white text-sm font-bold rounded">
          {error}
        </div>
      ) : (
        <></>
      )}
    </form>
  );
};

export default ModalInput;
